import { useTranslation } from "react-i18next";
import { Linkedin } from "lucide-react";
import type { Author } from "@/types/blog";

interface AuthorBlockProps {
  author: Author;
}

const AUTHOR_META: Record<
  Author,
  { name: string; role: string; initials: string; linkedin?: string }
> = {
  pinch: {
    name: "Pinch Guigui",
    role: "Co-Founder",
    initials: "PG",
    linkedin: "https://www.linkedin.com/in/pinchas-guigui-63b58a247",
  },
  ezra: {
    name: "Ezra Rothchild",
    role: "Co-Founder",
    initials: "ER",
  },
};

export function AuthorBlock({ author }: AuthorBlockProps) {
  const { t } = useTranslation();
  const meta = AUTHOR_META[author];
  const bio = t(`pages.blog.authorBio.${author}`);

  return (
    <section
      aria-labelledby={`author-${author}`}
      style={{
        marginTop: "var(--s-lg)",
        padding: 24,
        background: "var(--dyna-bg-elevated)",
        border: "1px solid var(--dyna-border)",
        borderRadius: 16,
        display: "flex",
        gap: 18,
        alignItems: "flex-start",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(90,70,102,0.6), rgba(156,139,194,0.4))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "\"Coolvetica\", system-ui, sans-serif",
          fontSize: 22,
          color: "var(--dyna-fg)",
          flexShrink: 0,
        }}
      >
        {meta.initials}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "0.14em",
            color: "var(--lavender-400)",
            marginBottom: 4,
          }}
        >
          {t("pages.blog.aboutAuthor")}
        </div>
        <h4
          id={`author-${author}`}
          style={{
            fontFamily: "Geist, system-ui, sans-serif",
            fontSize: 18,
            fontWeight: 600,
            margin: "0 0 2px",
            color: "var(--dyna-fg)",
          }}
        >
          {meta.name}
        </h4>
        <div
          style={{
            fontFamily: "Geist, system-ui, sans-serif",
            fontSize: 13,
            color: "var(--dyna-fg-muted)",
            marginBottom: 10,
          }}
        >
          {meta.role}
        </div>
        <p
          style={{
            fontFamily: "Geist, system-ui, sans-serif",
            fontSize: 14,
            lineHeight: 1.6,
            color: "var(--dyna-fg-muted)",
            margin: 0,
          }}
        >
          {bio}
        </p>
        {meta.linkedin && (
          <a
            href={meta.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginTop: 10,
              color: "var(--lavender-300)",
              fontSize: 13,
              textDecoration: "none",
            }}
          >
            <Linkedin size={14} />
            LinkedIn
          </a>
        )}
      </div>
    </section>
  );
}
